import React from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle2Icon,
  DownloadIcon,
  ShareIcon,
  SmartphoneIcon,
  MonitorIcon,
  ArrowRightIcon,
} from 'lucide-react'
import { usePwaInstall } from '../hooks/usePwaInstall'
import { mobile } from '../components/mobile'

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
        {n}
      </span>
      <span className="text-sm text-gray-700 leading-relaxed pt-0.5">{children}</span>
    </li>
  )
}

export function InstallApp() {
  const { platform, isInstalled, canPromptInstall, installing, promptInstall } = usePwaInstall()

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-blue-50 px-4 py-10 sm:py-14">
      <div className="max-w-md mx-auto space-y-6">
        <header className="text-center space-y-3">
          <img
            src="/pwa-192.png"
            alt="RBC Field"
            className="w-20 h-20 mx-auto rounded-2xl shadow-sm border border-gray-200/80 bg-white"
            width={80}
            height={80}
          />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">RBC Field</p>
            <h1 className="mt-1 text-2xl sm:text-[1.75rem] font-bold text-gray-900 tracking-tight">
              Install the app
            </h1>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              Add RBC Field to your home screen for faster access to your dashboard, jobs, and time
              clock — no App Store required.
            </p>
          </div>
        </header>

        <section className={`${mobile.surface} p-5 sm:p-6 space-y-5`}>
          {isInstalled ? (
            <div className="text-center space-y-3 py-2">
              <CheckCircle2Icon className="w-12 h-12 text-green-600 mx-auto" />
              <h2 className="text-lg font-semibold text-gray-900">You&apos;re all set</h2>
              <p className="text-sm text-gray-600">
                RBC Field is already installed. Open it from your home screen for the full app
                experience.
              </p>
              <Link to="/login" className={`${mobile.primaryBtn} w-full`}>
                Open sign in
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              {platform === 'ios' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-900">
                    <SmartphoneIcon className="w-5 h-5 text-blue-600" />
                    <h2 className="text-base font-semibold">iPhone / iPad (Safari)</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    iOS doesn&apos;t allow a one-tap install button. Use Safari&apos;s Share menu:
                  </p>
                  <ol className="space-y-3">
                    <Step n={1}>
                      Open this page in <strong>Safari</strong> (not Chrome).
                    </Step>
                    <Step n={2}>
                      Tap the <ShareIcon className="inline w-4 h-4 text-blue-600 align-text-bottom" />{' '}
                      <strong>Share</strong> button.
                    </Step>
                    <Step n={3}>
                      Scroll and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
                    </Step>
                  </ol>
                  <p className="text-xs text-gray-500">
                    After adding, launch <strong>RBC Field</strong> from your home screen.
                  </p>
                </div>
              )}

              {platform === 'android' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-900">
                    <SmartphoneIcon className="w-5 h-5 text-blue-600" />
                    <h2 className="text-base font-semibold">Android (Chrome)</h2>
                  </div>
                  {canPromptInstall ? (
                    <>
                      <p className="text-sm text-gray-600">
                        Install RBC Field for a full-screen app with home-screen and app-drawer
                        icons.
                      </p>
                      <button
                        type="button"
                        onClick={() => void promptInstall()}
                        disabled={installing}
                        className={`${mobile.primaryBtn} w-full`}
                      >
                        <DownloadIcon className="w-4 h-4" />
                        {installing ? 'Opening install…' : 'Install app'}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        If the install button isn&apos;t ready yet, use Chrome&apos;s menu:
                      </p>
                      <ol className="space-y-3">
                        <Step n={1}>
                          Tap the <strong>⋮</strong> menu in Chrome.
                        </Step>
                        <Step n={2}>
                          Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.
                        </Step>
                        <Step n={3}>Confirm, then open <strong>RBC Field</strong> from your home screen.</Step>
                      </ol>
                      <p className="text-xs text-gray-500">
                        Tip: use the production HTTPS site so install and offline shell work
                        reliably.
                      </p>
                    </>
                  )}
                </div>
              )}

              {platform === 'desktop' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-900">
                    <MonitorIcon className="w-5 h-5 text-blue-600" />
                    <h2 className="text-base font-semibold">Desktop</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    For field use, open{' '}
                    <span className="font-medium text-gray-800">https://app.rbcfs.com/install</span>{' '}
                    on your phone and follow the on-screen steps.
                  </p>
                  {canPromptInstall ? (
                    <>
                      <p className="text-sm text-gray-600">
                        Your browser also supports installing RBC Field on this computer:
                      </p>
                      <button
                        type="button"
                        onClick={() => void promptInstall()}
                        disabled={installing}
                        className={`${mobile.primaryBtn} w-full`}
                      >
                        <DownloadIcon className="w-4 h-4" />
                        {installing ? 'Opening install…' : 'Install app'}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">
                      On Chromium browsers (Chrome / Edge), look for an install icon in the address
                      bar, or use the browser menu → <strong>Install RBC Field</strong>.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {!isInstalled && (
          <div className="space-y-3">
            <Link to="/login" className={`${mobile.secondaryBtn} w-full`}>
              Sign in to the app
            </Link>
            <p className="text-center text-sm text-gray-500">
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                Continue in browser
              </Link>
            </p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400">
          Works best on HTTPS · Same account as the web app
        </p>
      </div>
    </div>
  )
}
